import { msg } from '@lingui/core/macro';
import { i18n } from '../i18n';
import * as Sentry from '@sentry/react';

/**
 * Standardized error types for the application
 */
export enum ErrorType {
  NETWORK = 'NETWORK',
  PERMISSION = 'PERMISSION',
  VALIDATION = 'VALIDATION',
  UPLOAD = 'UPLOAD',
  GEOLOCATION = 'GEOLOCATION',
  CAMERA = 'CAMERA',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Application error with type and user-friendly message
 */
export class AppError extends Error {
  constructor(
    public type: ErrorType,
    public userMessage: string,
    public originalError?: unknown
  ) {
    super(userMessage);
    this.name = 'AppError';

    // Log technical details in development
    if (process.env.NODE_ENV === 'development' && originalError) {
      console.error('[AppError]', { type, userMessage, originalError });
    }

    // Send to Sentry with context
    Sentry.captureException(originalError || this, {
      tags: {
        errorType: type,
        userFacing: 'true',
      },
      contexts: {
        appError: {
          type,
          userMessage,
          locale: i18n.locale,
        },
      },
      level: this.getSentryLevel(type),
    });
  }

  /**
   * Map error type to Sentry severity level
   */
  private getSentryLevel(type: ErrorType): Sentry.SeverityLevel {
    switch (type) {
      case ErrorType.NETWORK:
        return 'warning';
      case ErrorType.PERMISSION:
        return 'info'; // User-initiated denial, not a critical error
      case ErrorType.GEOLOCATION:
        return 'info'; // Often user-denied or device limitation
      case ErrorType.CAMERA:
        return 'info'; // Often user-denied or device limitation
      case ErrorType.VALIDATION:
        return 'warning';
      case ErrorType.UPLOAD:
        return 'error';
      case ErrorType.UNKNOWN:
      default:
        return 'error';
    }
  }
}

/**
 * Error details with message and optional error code
 */
export interface ErrorDetails {
  message: string;
  code?: string;
}

/** Field-level issue from API validation (`details[]` or legacy Zod). */
export type ApiFieldIssue = {
  path: string;
  message: string;
};

/**
 * Full parse of an API error JSON body — single place for message, code, and field issues.
 */
export type ParsedApiErrorBody = {
  message: string;
  code?: string;
  fieldIssues: ApiFieldIssue[];
};

function normalizeApiPath(path: string): string | null {
  const trimmed = path.trim();
  if (!trimmed || trimmed === '(root)') {
    return null;
  }
  return trimmed;
}

function fieldIssuesFromDetails(details: unknown): ApiFieldIssue[] {
  if (!Array.isArray(details)) {
    return [];
  }
  const issues: ApiFieldIssue[] = [];
  for (const item of details) {
    if (!item || typeof item !== 'object') continue;
    const path = typeof (item as { path?: unknown }).path === 'string' ? (item as { path: string }).path : '';
    const message =
      typeof (item as { message?: unknown }).message === 'string'
        ? (item as { message: string }).message
        : 'Invalid value';
    const normalized = normalizeApiPath(path);
    if (normalized) {
      issues.push({ path: normalized, message });
    }
  }
  return issues;
}

/**
 * Parse legacy OpenAPI/Zod body where issues are a JSON string under error.message.
 * // TODO: remove after backend VALIDATION_ERROR rollout — once deployed, this shape
 * cannot occur again and this branch is dead weight.
 */
function parseLegacyZodIssues(raw: string): { message: string; fieldIssues: ApiFieldIssue[] } | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('[')) {
    return null;
  }
  try {
    const issues = JSON.parse(trimmed) as Array<{ path?: Array<string | number>; message?: string }>;
    if (!Array.isArray(issues) || issues.length === 0) {
      return null;
    }
    const fieldIssues: ApiFieldIssue[] = [];
    const summaryParts: string[] = [];
    for (const issue of issues) {
      const path = Array.isArray(issue.path) && issue.path.length > 0 ? issue.path.map(String).join('.') : '(root)';
      const msg = issue.message ?? 'Invalid value';
      summaryParts.push(`${path}: ${msg}`);
      const normalized = normalizeApiPath(path);
      if (normalized) {
        fieldIssues.push({ path: normalized, message: msg });
      }
    }
    return { message: summaryParts.join('; '), fieldIssues };
  } catch {
    return null;
  }
}

/**
 * Single parser for API error response bodies.
 * Supports handler errors ({ error, message }), validation ({ error, message, details }),
 * and legacy nested Zod shapes.
 */
export function parseApiErrorBody(data: unknown, fallback: string): ParsedApiErrorBody {
  if (!data || typeof data !== 'object') {
    return { message: fallback, fieldIssues: [] };
  }

  const body = data as {
    message?: unknown;
    error?: unknown;
    details?: unknown;
  };

  let code: string | undefined;
  if (typeof body.error === 'string' && body.error.trim()) {
    code = body.error;
  }

  const fieldIssues = fieldIssuesFromDetails(body.details);

  // Prefer top-level message (handler errors + new VALIDATION_ERROR format)
  if (typeof body.message === 'string' && body.message.trim()) {
    return { message: body.message, code, fieldIssues };
  }

  // TODO: remove after backend VALIDATION_ERROR rollout — legacy
  // { success: false, error: { name: 'ZodError', message: '[...]' } }
  if (body.error && typeof body.error === 'object') {
    const nested = body.error as { name?: string; message?: unknown };
    if (nested.name === 'ZodError') {
      code = code ?? 'VALIDATION_ERROR';
    }
    if (typeof nested.message === 'string' && nested.message.trim()) {
      const legacy = parseLegacyZodIssues(nested.message);
      if (legacy) {
        return {
          message: legacy.message,
          code: code ?? (nested.name === 'ZodError' ? 'VALIDATION_ERROR' : undefined),
          fieldIssues: fieldIssues.length > 0 ? fieldIssues : legacy.fieldIssues,
        };
      }
      return {
        message: nested.message,
        code: code ?? (nested.name === 'ZodError' ? 'VALIDATION_ERROR' : undefined),
        fieldIssues,
      };
    }
  }

  if (code) {
    return { message: code, code, fieldIssues };
  }

  return { message: fallback, fieldIssues };
}

/**
 * Extract a human-readable message from an API error response body.
 */
export function getApiErrorMessageFromData(data: unknown, fallback: string): { message: string; code?: string } {
  const parsed = parseApiErrorBody(data, fallback);
  return { message: parsed.message, code: parsed.code };
}

/** Safari's message when a canvas or image decode exceeds the tab memory budget. */
export function isLowMemoryError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return /low memory/i.test(message);
}

function lowMemoryMessage(): string {
  return i18n._(
    msg`The photo could not be processed because the browser ran out of memory. Close other tabs and try again.`
  );
}

/**
 * Extract error details (message and code) from various error types
 */
export function getErrorDetails(error: unknown): ErrorDetails {
  if (error instanceof AppError) {
    return { message: error.userMessage };
  }

  if (typeof error === 'string') {
    return { message: isLowMemoryError(error) ? lowMemoryMessage() : error };
  }

  // Check for Axios/API errors BEFORE generic Error instances
  // Axios errors are Error instances but have additional response data
  if (error && typeof error === 'object') {
    const apiError = error as any;

    // First check for API response body
    if (apiError.response?.data) {
      const fallback = apiError.message || i18n._(msg`An unexpected error occurred`);
      return getApiErrorMessageFromData(apiError.response.data, fallback);
    }

    // Then check for generic error message
    if (apiError.message) {
      return { message: isLowMemoryError(apiError.message) ? lowMemoryMessage() : apiError.message };
    }
  }

  if (error instanceof Error) {
    return { message: isLowMemoryError(error) ? lowMemoryMessage() : error.message };
  }

  return { message: i18n._(msg`An unexpected error occurred`) };
}

/**
 * Prefer API error body message; fall back to a localized/admin string.
 */
export function getAdminApiErrorMessage(error: unknown, fallback: string): string {
  const details = getErrorDetails(error);
  // Axios generic status text is useless; use fallback instead
  if (details.message.startsWith('Request failed with status code') || details.message === 'Network Error') {
    return fallback;
  }
  return details.message || fallback;
}

/**
 * Get Axios response data if present.
 */
export function getAxiosResponseData(error: unknown): unknown {
  if (error && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { data?: unknown } }).response?.data;
  }
  return undefined;
}

/**
 * Extract user-friendly error message from various error types
 * For cases where you also need the error code, use getErrorDetails() instead
 */
export function getErrorMessage(error: unknown): string {
  return getErrorDetails(error).message;
}

/**
 * Handle geolocation errors with user-friendly messages
 */
export function handleGeolocationError(error: GeolocationPositionError): AppError {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return new AppError(
        ErrorType.GEOLOCATION,
        i18n._(msg`Location access denied. Please allow location permissions.`),
        error
      );
    case error.TIMEOUT:
      return new AppError(
        ErrorType.GEOLOCATION,
        i18n._(msg`Location request timed out. Please check device settings.`),
        error
      );
    case error.POSITION_UNAVAILABLE:
    default:
      return new AppError(
        ErrorType.GEOLOCATION,
        i18n._(msg`Cannot access location. Please check device settings.`),
        error
      );
  }
}

/**
 * Handle camera/media errors with user-friendly messages
 */
export function handleCameraError(error: unknown): AppError {
  const err = error as any;

  if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
    return new AppError(ErrorType.CAMERA, i18n._(msg`Camera access denied. Please allow camera permissions.`), error);
  }

  if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') {
    return new AppError(ErrorType.CAMERA, i18n._(msg`No camera found. Please check device.`), error);
  }

  return new AppError(ErrorType.CAMERA, i18n._(msg`Camera access failed. Please check permissions.`), error);
}
